import type { DeviceRoster } from '@pms/shared';
import { Delete, Lock, Tablet, User } from 'lucide-react';
import { useEffect, useState, type FormEvent } from 'react';
import { Navigate, useNavigate, useSearchParams } from 'react-router';
import { Badge, Button, ErrorBox, Spinner } from '@/components/ui';
import { ApiError, errorMessage, post } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { forgetDevice, getDevice } from '@/lib/device';
import { cls } from '@/lib/format';

export function LoginPage() {
  const { user, ready } = useAuth();
  const [params] = useSearchParams();
  const next = params.get('next') || '/';
  // A registered group tablet shows its people and a PIN pad; anyone can still use a password.
  const [usePassword, setUsePassword] = useState<{ username: string; note?: string } | null>(getDevice() ? null : { username: '' });

  if (ready && user) {
    // On a group tablet, someone without a PIN yet is taken straight to setting one.
    const toPin = !!getDevice() && user.pinAllowed && !user.pinSet;
    return <Navigate to={toPin ? '/account/security?pin=1' : next} replace state={{ signedIn: true }} />;
  }

  return (
    <div className="grid min-h-screen lg:grid-cols-[1.1fr_1fr]">
      <div className="relative hidden overflow-hidden bg-brand-950 lg:block">
        <div className="absolute inset-0 opacity-[0.07]" style={{ backgroundImage: 'repeating-linear-gradient(45deg, #fbbf24 0 1px, transparent 1px 22px), repeating-linear-gradient(-45deg, #fbbf24 0 1px, transparent 1px 22px)' }} />
        <div className="relative flex h-full flex-col justify-between p-12 text-brand-100">
          <div className="flex items-center gap-3">
            <div className="grid size-10 place-items-center rounded-xl bg-brand-800 text-lg font-bold text-gold-400 ring-1 ring-gold-500/40">H</div>
            <span className="text-sm font-medium tracking-wide text-brand-200">Property Management System</span>
          </div>
          <div>
            <h1 className="max-w-md text-4xl font-semibold leading-tight tracking-tight text-white">Every room, folio and naira — in one place.</h1>
            <p className="mt-4 max-w-md text-brand-300">Reservations, front desk, housekeeping, outlets, events and hotel accounting for a five-star operation.</p>
          </div>
          <div className="text-xs text-brand-400">Authorised staff only. All activity is recorded.</div>
        </div>
      </div>
      <div className="flex items-center justify-center p-6">
        {usePassword ? (
          <PasswordSignIn next={next} initialUsername={usePassword.username} note={usePassword.note} onTablet={getDevice() ? () => setUsePassword(null) : undefined} />
        ) : (
          <TabletSignIn next={next} onPassword={(username, note) => setUsePassword({ username, note })} />
        )}
      </div>
    </div>
  );
}

function PasswordSignIn({ next, initialUsername, note, onTablet }: { next: string; initialUsername: string; note?: string; onTablet?: () => void }) {
  const { login, verifyMfa } = useAuth();
  const navigate = useNavigate();
  const [username, setUsername] = useState(initialUsername);
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [mfaToken, setMfaToken] = useState<string | null>(null);
  const [code, setCode] = useState('');

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      if (mfaToken) {
        await verifyMfa(mfaToken, code.trim());
      } else {
        const challenge = await login(username.trim(), password);
        if (challenge) {
          setMfaToken(challenge);
          return;
        }
      }
      navigate(next, { replace: true, state: { signedIn: true } });
    } catch (err) {
      if (mfaToken && /expired|timed out/i.test(errorMessage(err))) setMfaToken(null);
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <form onSubmit={submit} className="w-full max-w-sm">
      <h2 className="text-2xl font-semibold tracking-tight">Sign in</h2>
      <p className="mt-1 text-sm text-slate-500">{mfaToken ? 'Enter the 6-digit code from your authenticator app, or one of your recovery codes.' : note ?? 'Use the credentials issued by your system administrator.'}</p>
      {mfaToken ? (
        <div className="mt-8 space-y-4">
          <input
            autoFocus
            inputMode="numeric"
            autoComplete="one-time-code"
            value={code}
            onChange={(e) => setCode(e.target.value.toUpperCase().replace(/[^0-9A-Z-]/g, '').slice(0, 9))}
            className="h-12 w-full rounded-lg border border-slate-300 text-center font-mono text-2xl tracking-[0.4em] focus:border-brand-600 focus:outline-none focus:ring-2 focus:ring-brand-600/20"
          />
          {error && <ErrorBox error={new Error(error)} />}
          <Button type="submit" variant="primary" className="h-10 w-full" loading={busy} disabled={code.length < 6}>
            Confirm
          </Button>
          <button type="button" className="w-full text-center text-xs text-slate-500 hover:underline" onClick={() => (setMfaToken(null), setCode(''), setError(null))}>
            Back to password
          </button>
        </div>
      ) : (
        <div className="mt-8 space-y-4">
          <label className="block">
            <span className="mb-1 block text-xs font-medium text-slate-600">Username</span>
            <div className="relative">
              <User className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-slate-400" />
              <input autoFocus={!initialUsername} autoComplete="username" value={username} onChange={(e) => setUsername(e.target.value)} className="h-10 w-full rounded-lg border border-slate-300 pl-9 pr-3 focus:border-brand-600 focus:outline-none focus:ring-2 focus:ring-brand-600/20" />
            </div>
          </label>
          <label className="block">
            <span className="mb-1 block text-xs font-medium text-slate-600">Password</span>
            <div className="relative">
              <Lock className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-slate-400" />
              <input autoFocus={!!initialUsername} type="password" autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} className="h-10 w-full rounded-lg border border-slate-300 pl-9 pr-3 focus:border-brand-600 focus:outline-none focus:ring-2 focus:ring-brand-600/20" />
            </div>
          </label>
          {error && <ErrorBox error={new Error(error)} />}
          <Button type="submit" variant="primary" className="h-10 w-full" loading={busy} disabled={!username || !password}>
            Sign in
          </Button>
        </div>
      )}
      {onTablet && (
        <button type="button" onClick={onTablet} className="mt-4 flex w-full items-center justify-center gap-1.5 text-center text-sm font-medium text-brand-700 hover:underline">
          <Tablet className="size-4" /> Back to the group's names
        </button>
      )}
      <a href="/emergency" className="mt-6 block text-center text-xs text-slate-500 hover:underline">
        System unreachable? Open this computer's emergency list
      </a>
    </form>
  );
}

/** A registered group tablet: tap your name, then your PIN. */
function TabletSignIn({ next, onPassword }: { next: string; onPassword: (username: string, note?: string) => void }) {
  const { pinLogin } = useAuth();
  const navigate = useNavigate();
  const [roster, setRoster] = useState<DeviceRoster | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [person, setPerson] = useState<DeviceRoster['people'][number] | null>(null);
  const [showOthers, setShowOthers] = useState(false);
  const [pin, setPin] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const device = getDevice();
    if (!device) return;
    post<DeviceRoster>('/auth/device/roster', { deviceToken: device.token })
      .then(setRoster)
      .catch((e: unknown) => {
        if (e instanceof ApiError && e.code === 'DEVICE_UNKNOWN') {
          forgetDevice();
          onPassword('', 'This tablet was withdrawn from PIN sign-in. Sign in with your username and password.');
        } else setLoadError(errorMessage(e));
      });
  }, [onPassword]);

  async function submit() {
    if (!person || pin.length < 4) return;
    setBusy(true);
    setError(null);
    try {
      await pinLogin(person.id, pin);
      navigate(next, { replace: true, state: { signedIn: true } });
    } catch (e) {
      setPin('');
      setError(errorMessage(e));
    } finally {
      setBusy(false);
    }
  }

  // Keyboard too (tablets with a keyboard cover, or testing on a computer).
  useEffect(() => {
    if (!person) return;
    const onKey = (e: KeyboardEvent) => {
      if (/^\d$/.test(e.key)) setPin((p) => (p.length < 6 ? p + e.key : p));
      else if (e.key === 'Backspace') setPin((p) => p.slice(0, -1));
      else if (e.key === 'Enter') void submit();
      else if (e.key === 'Escape') (setPerson(null), setPin(''), setError(null));
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  });

  if (loadError) {
    return (
      <div className="w-full max-w-sm space-y-4">
        <ErrorBox error={new Error(loadError)} />
        <Button className="w-full" onClick={() => onPassword('')}>
          Sign in with username and password
        </Button>
      </div>
    );
  }
  if (!roster) return <Spinner label="Loading the tablet…" />;
  const group = roster.people.filter((p) => roster.device.groupCode && p.groupCode === roster.device.groupCode);
  const others = roster.people.filter((p) => !group.includes(p));

  if (person) {
    return (
      <div className="w-full max-w-xs">
        <button type="button" className="text-sm text-slate-500 hover:underline" onClick={() => (setPerson(null), setPin(''), setError(null))}>
          ← Not {person.fullName.split(' ')[0]}?
        </button>
        <h2 className="mt-3 text-2xl font-semibold tracking-tight">{person.fullName}</h2>
        <p className="mt-1 text-sm text-slate-500">Enter your PIN</p>
        <div className="my-6 flex justify-center gap-3" aria-label={`${pin.length} digits entered`}>
          {Array.from({ length: Math.max(4, pin.length) }, (_, i) => (
            <span key={i} className={cls('size-4 rounded-full ring-2 ring-brand-700', i < pin.length ? 'bg-brand-700' : 'bg-white')} />
          ))}
        </div>
        {error && (
          <div className="mb-3">
            <ErrorBox error={new Error(error)} />
          </div>
        )}
        <div className="grid grid-cols-3 gap-3">
          {['1', '2', '3', '4', '5', '6', '7', '8', '9'].map((d) => (
            <button key={d} type="button" disabled={busy} onClick={() => setPin((p) => (p.length < 6 ? p + d : p))} className="h-16 rounded-xl bg-white text-2xl font-semibold shadow-sm ring-1 ring-slate-200 active:bg-slate-100">
              {d}
            </button>
          ))}
          <button type="button" aria-label="Delete" onClick={() => setPin((p) => p.slice(0, -1))} className="grid h-16 place-items-center rounded-xl bg-slate-50 ring-1 ring-slate-200 active:bg-slate-100">
            <Delete className="size-6 text-slate-600" />
          </button>
          <button type="button" disabled={busy} onClick={() => setPin((p) => (p.length < 6 ? `${p}0` : p))} className="h-16 rounded-xl bg-white text-2xl font-semibold shadow-sm ring-1 ring-slate-200 active:bg-slate-100">
            0
          </button>
          <Button variant="primary" className="h-16 rounded-xl text-lg" loading={busy} disabled={pin.length < 4} onClick={() => void submit()}>
            OK
          </Button>
        </div>
        <button type="button" className="mt-5 w-full text-center text-xs text-slate-500 hover:underline" onClick={() => onPassword(person.username, 'Forgot your PIN? Sign in with your password; the head of housekeeping can clear a PIN.')}>
          Use my password instead
        </button>
      </div>
    );
  }

  const tile = (p: DeviceRoster['people'][number]) => (
    <button
      key={p.id}
      type="button"
      onClick={() => (p.pinSet ? setPerson(p) : onPassword(p.username, 'No PIN yet: sign in once with your password, then choose your PIN.'))}
      className="flex min-h-20 flex-col items-start justify-center rounded-xl bg-white px-4 py-3 text-left shadow-sm ring-1 ring-slate-200 hover:ring-brand-600 active:bg-slate-50"
    >
      <span className="text-[17px] font-semibold text-slate-900">{p.fullName}</span>
      <span className="mt-1 flex gap-1.5">
        {!p.pinSet && <Badge tone="amber">No PIN yet</Badge>}
      </span>
    </button>
  );
  return (
    <div className="w-full max-w-md">
      <div className="flex items-center gap-2 text-sm font-medium text-brand-700">
        <Tablet className="size-4" /> {roster.device.name}
      </div>
      <h2 className="mt-1 text-2xl font-semibold tracking-tight">{roster.device.groupName ? `${roster.device.groupName}: who is signing in?` : 'Who is signing in?'}</h2>
      <p className="mt-1 text-sm text-slate-500">Tap your name, then enter your PIN.</p>
      <div className="mt-6 grid gap-3 sm:grid-cols-2">{(group.length ? group : others).map(tile)}</div>
      {group.length > 0 && others.length > 0 && (
        <div className="mt-5">
          <button type="button" className="text-sm font-medium text-slate-600 hover:underline" onClick={() => setShowOthers(!showOthers)}>
            {showOthers ? 'Hide' : 'Show'} other housekeeping staff ({others.length})
          </button>
          {showOthers && <div className="mt-3 grid gap-3 sm:grid-cols-2">{others.map(tile)}</div>}
        </div>
      )}
      <button type="button" className="mt-6 block text-center text-xs text-slate-500 hover:underline" onClick={() => onPassword('')}>
        Sign in with username and password
      </button>
    </div>
  );
}
